import * as _ from "lodash";
import { createSelector } from "reselect";
import { ListingTypes } from "office/reports/stats/StatsReport";
import * as utils from "office/reports/stats/utils/utils";
//=================================================================================================================================================
//-- Mapping Dto to Domain helpers --------------------------------------------------------------------------------------------------
//=================================================================================================================================================
//-- Map Graph Dto to Domain Dto ------------------------------------------
//-- Mapping Graph Dto to Domain Chart
const mapGraphToChart = (dtoGraph, colour, fromDate, toDate) => {
    let series = [];
    //let seriesCols: Array<string> = [];
    let summaryValue = null;
    // Clone the series that will be rendered
    series.push(_.cloneDeep(dtoGraph.points));
    //seriesCols.push(colour);
    summaryValue = dtoGraph.summaryValue;
    // Create the days series (the x-axis labels)
    let daysSeries = utils.createDaySeries(fromDate, toDate);
    series.unshift([...daysSeries]);
    //-- Merging the seperate arrays into one (needed data shapefor the google graphs)
    // "series" contains data of the form: [[Monday, Tuesday][2.3, 5.9][7.8, 3.4]]
    // "data" (the merged arrays) is of the form [[[Monday][2.3][7.8]],[[Tuesday][5.9][3.4]]]
    let data = utils.interweave(series);
    return {
        colour: colour,
        summaryValue: summaryValue,
        series: data
    };
};
//-- Map one of the Sales\Rental\All Graphs in the Dto to a single Sale OR Rental Or All Domain Chart
const mapFilteredGraphToChart = (graph, filterByType, fromDate, toDate) => {
    let series = null;
    let colour = null;
    switch (filterByType) {
        case ListingTypes.Sales:
            series = graph.sales;
            colour = utils._colourSales;
            break;
        case ListingTypes.Rentals:
            series = graph.rentals;
            colour = utils._colourRentals;
            break;
        default:
            series = graph.total;
            colour = utils._colourAll;
    }
    return mapGraphToChart(series, colour, fromDate, toDate);
};
//-- Map a Summary Graph Dto to a Domain Chart
const mapSummaryGraph = (graph, fromDate, toDate) => {
    return {
        listingsCountGraph: mapGraphToChart(graph.listingsCountGraph, utils._colourAll, fromDate, toDate),
        totalLeadsGraph: mapGraphToChart(graph.totalLeadsGraph, utils._colourAll, fromDate, toDate),
        totalViewsGraph: mapGraphToChart(graph.totalViewsGraph, utils._colourAll, fromDate, toDate)
    };
};
//-- Map a Stats Graph Dto to a Domain Chart
const mapStatsGraph = (graph, filterByType, fromDate, toDate) => {
    return {
        listingsCountGraph: mapFilteredGraphToChart(graph.listingsCountGraph, filterByType, fromDate, toDate),
        totalLeadsGraph: mapFilteredGraphToChart(graph.totalLeadsGraph, filterByType, fromDate, toDate),
        messageLeadsGraph: mapFilteredGraphToChart(graph.messageLeadsGraph, filterByType, fromDate, toDate),
        phoneLeadsGraph: mapFilteredGraphToChart(graph.phoneLeadsGraph, filterByType, fromDate, toDate),
        totalViewsGraph: mapFilteredGraphToChart(graph.totalViewsGraph, filterByType, fromDate, toDate)
    };
};
//-- Map a Performance Graph Dto to a Domain Chart
const mapPerfGraph = (graph, filterByType, fromDate, toDate) => {
    return {
        listingsCountGraph: mapFilteredGraphToChart(graph.listingsCountGraph, filterByType, fromDate, toDate),
        avgStandardViewsGraph: mapFilteredGraphToChart(graph.avgStandardViewsGraph, filterByType, fromDate, toDate),
        avgFeaturedViewsGraph: mapFilteredGraphToChart(graph.avgFeaturedViewsGraph, filterByType, fromDate, toDate),
        avgHdViewsGraph: mapFilteredGraphToChart(graph.avgHdViewsGraph, filterByType, fromDate, toDate),
        avgFeaturedHdViewsGraph: mapFilteredGraphToChart(graph.avgFeaturedHdViewsGraph, filterByType, fromDate, toDate),
        avgStandardLeadsGraph: mapFilteredGraphToChart(graph.avgStandardLeadsGraph, filterByType, fromDate, toDate),
        avgFeaturedLeadsGraph: mapFilteredGraphToChart(graph.avgFeaturedLeadsGraph, filterByType, fromDate, toDate),
        avgHdLeadsGraph: mapFilteredGraphToChart(graph.avgHdLeadsGraph, filterByType, fromDate, toDate),
        avgFeaturedHdLeadsGraph: mapFilteredGraphToChart(graph.avgFeaturedHdLeadsGraph, filterByType, fromDate, toDate)
    };
};
//-- Map a Developments Graph Dto to a Domain Chart
const mapDevelopmentsGraph = (graph, fromDate, toDate) => {
    return {
        totalLeadsGraph: mapGraphToChart(graph.totalLeadsGraph, utils._colourAll, fromDate, toDate),
        totalViewsGraph: mapGraphToChart(graph.totalViewsGraph, utils._colourAll, fromDate, toDate)
    };
};
//-- Map a Billboards Graph Dto to a Domain Chart
const mapBillboardStatsGraph = (graph, filterByType, fromDate, toDate) => {
    return {
        suburbsCountGraph: mapFilteredGraphToChart(graph.suburbsCountGraph, filterByType, fromDate, toDate),
        totalLeadsGraph: mapFilteredGraphToChart(graph.totalLeadsGraph, filterByType, fromDate, toDate),
        messageLeadsGraph: mapFilteredGraphToChart(graph.messageLeadsGraph, filterByType, fromDate, toDate),
        phoneLeadsGraph: mapFilteredGraphToChart(graph.phoneLeadsGraph, filterByType, fromDate, toDate),
        totalImpressionsGraph: mapFilteredGraphToChart(graph.totalImpressionsGraph, filterByType, fromDate, toDate)
    };
};
//=================================================================================================================================================
//-- Map Data Dto to Domain Dto ------------------------------------------
//=================================================================================================================================================
//-- Map Developments Data Dto to Domain data
const mapDevelopmentsData = (developmentsDataDto) => {
    return developmentsDataDto.map((dto) => {
        return { id: dto.id,
            name: dto.name,
            suburb: dto.suburb,
            leadsAndReveals: dto.leads + dto.reveals,
            views: dto.views };
    });
};
//-- Map Billboards Data Dto to Domain data
const mapBillboardsData = (billboardsDataDto) => {
    return billboardsDataDto.map((dto) => {
        return {
            id: dto.id,
            suburb: dto.suburb,
            listingType: ListingTypes[dto.listingType],
            leadsAndReveals: dto.leads + dto.reveals,
            impressions: dto.impressions
        };
    });
};
const getData = (statsReport, fromDate, toDate) => {
    if (!statsReport) {
        return null;
    }
    return {
        summaryData: statsReport.summaryData,
        summaryStatsGraphs: mapSummaryGraph(statsReport.summaryStatsGraphs, fromDate, toDate),
        summaryListingPerfGraphs: mapPerfGraph(statsReport.listingsPerfGraphs, ListingTypes.All, fromDate, toDate),
        salesStatsGraphs: mapStatsGraph(statsReport.listingsStatsGraphs, ListingTypes.Sales, fromDate, toDate),
        salesPerfGraphs: mapPerfGraph(statsReport.listingsPerfGraphs, ListingTypes.Sales, fromDate, toDate),
        rentalsStatsGraphs: mapStatsGraph(statsReport.listingsStatsGraphs, ListingTypes.Rentals, fromDate, toDate),
        rentalsPerfGraphs: mapPerfGraph(statsReport.listingsPerfGraphs, ListingTypes.Rentals, fromDate, toDate),
        developmentsData: mapDevelopmentsData(statsReport.developmentsData),
        developmentsSummaryGraphs: mapDevelopmentsGraph(statsReport.developmentsSummaryGraphs, fromDate, toDate),
        billboardData: mapBillboardsData(statsReport.billboardData),
        billboardStatsGraphs: mapBillboardStatsGraph(statsReport.billboardStatsGraphs, ListingTypes.All, fromDate, toDate)
    };
};
//=================================================================================================================================================
//-- Selector --------------------------------------------------------------------------------------------------
//=================================================================================================================================================
const stateReportSelector = (state) => state.reports.officeReports.officeStats;
export const reportSelector = createSelector(stateReportSelector, (state) => {
    if (!state) {
        return {
            isFetching: true,
            isError: false,
            reportData: null
        };
    }
    let { statsReport } = state;
    let { fromDate, toDate } = state;
    return {
        isFetching: state.isFetching,
        isError: state.isError,
        reportData: {
            officeId: state.officeId,
            officeName: state.officeName,
            fromDate: state.fromDate,
            toDate: state.toDate,
            data: getData(statsReport, fromDate, toDate)
        }
    };
});
export default reportSelector;
